import axios, {
  AxiosHeaders,
  type AxiosError,
  type InternalAxiosRequestConfig,
} from "axios";
import { readCsrfTokenCookie } from "./auth";

const UNSAFE_METHODS = new Set(["post", "put", "patch", "delete"]);
const REFRESH_EXCLUDED_PREFIXES = [
  "/buyer/",
  "/auth/login",
  "/auth/register",
  "/auth/password/",
  "/auth/verify-email/",
  "/auth/refresh",
  "/auth/logout",
];

export const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL ?? "/api",
  withCredentials: true,
});

const authApi = axios.create({
  baseURL: import.meta.env.VITE_API_URL ?? "/api",
  withCredentials: true,
});

type RetryableRequestConfig = InternalAxiosRequestConfig & {
  _landnestRetried?: boolean;
  _skipLandnestRefresh?: boolean;
};

let refreshPromise: Promise<void> | null = null;

api.interceptors.request.use((config) => {
  if (config.method && UNSAFE_METHODS.has(config.method.toLowerCase())) {
    const csrfToken = readCsrfTokenCookie();
    if (csrfToken) {
      const headers = new AxiosHeaders(config.headers);
      headers.set("x-csrf-token", csrfToken);
      config.headers = headers;
    }
  }

  return config;
});

function shouldRefreshRequest(error: AxiosError) {
  if (error.response?.status !== 401) {
    return false;
  }

  const config = error.config as RetryableRequestConfig | undefined;
  if (!config || config._landnestRetried || config._skipLandnestRefresh) {
    return false;
  }

  const url = config.url ?? "";
  return !REFRESH_EXCLUDED_PREFIXES.some((prefix) => url.startsWith(prefix));
}

async function refreshSession() {
  if (!refreshPromise) {
    refreshPromise = authApi
      .post("/auth/refresh", undefined, {
        _skipLandnestRefresh: true,
      } as RetryableRequestConfig)
      .then(() => undefined)
      .finally(() => {
        refreshPromise = null;
      });
  }

  return refreshPromise;
}

/**
 * Once a refresh attempt fails we treat the session as dead and force
 * the user back to /login. Without this the original request still
 * threw a 401 but the UI kept rendering, producing the "page not
 * available until I refresh and relogin" symptom the rep flagged.
 *
 * Guarded so we only redirect from a protected page (skip /login,
 * /register, /buyer/* and similar public surfaces where a 401 is
 * expected and shouldn't bounce the user).
 */
function forceLogoutRedirect() {
  if (typeof window === "undefined") return;
  const path = window.location.pathname;
  const isPublicAuthRoute =
    path === "/" ||
    path.startsWith("/login") ||
    path.startsWith("/register") ||
    path.startsWith("/forgot-password") ||
    path.startsWith("/reset-password") ||
    path.startsWith("/verify-email") ||
    path.startsWith("/buyer/login") ||
    path.startsWith("/buyer/register") ||
    path.startsWith("/buyer/subscribe");
  if (isPublicAuthRoute) return;
  // Use full reload so React Query cache + any in-memory user state
  // is cleared, and the root loader re-runs /auth/me on the way in.
  window.location.replace(`/login?reason=session_expired&from=${encodeURIComponent(path)}`);
}

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    if (!shouldRefreshRequest(error)) {
      throw error;
    }

    const config = error.config as RetryableRequestConfig;
    config._landnestRetried = true;

    try {
      await refreshSession();
      // Re-read CSRF token after refresh (new token is set by refresh response)
      const freshCsrfToken = readCsrfTokenCookie();
      if (freshCsrfToken && config.method && UNSAFE_METHODS.has(config.method.toLowerCase())) {
        const headers = new AxiosHeaders(config.headers);
        headers.set("x-csrf-token", freshCsrfToken);
        config.headers = headers;
      }
      return api(config);
    } catch {
      forceLogoutRedirect();
      throw error;
    }
  },
);